import styles from 'css/src/components/app/template-preview/BlueprintPreviewTile.module.scss';

import React, { useCallback } from 'react';
import classNames from 'classnames';

import Tooltip, { Placement } from '../../../common/ui/Tooltip';

import type { NarrativeThreadEnum } from 'js/src/client';
import { getAspectRatioFromLuminaryCanvasPreview } from 'js/src/components/common/ui/CanvasPreview';
import { StackedCanvasPreview } from 'js/src/components/common/ui/StackedCanvasPreview';
import { NARRATIVE_THREAD_DISPLAY_STRING_MAP, NARRATIVE_THREAD_ICON_MAP } from 'js/src/libs/consts';

export const BlueprintPreviewTile = ({
    name,
    duration,
    canvasPreview,
    narrativeThread,
    isSuggestedBlueprint = false,
    onTileClick,
    className,
    buttonText = 'Preview',
    previewImageUrl,
}: {
    name: string;
    duration?: string;
    canvasPreview: HTMLCanvasElement | undefined;
    narrativeThread?: NarrativeThreadEnum;
    isSuggestedBlueprint?: boolean;
    onTileClick: VoidFunction;
    className?: string;
    buttonText?: string;
    previewImageUrl?: string;
}) => {
    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const aspectRatio = getAspectRatioFromLuminaryCanvasPreview(canvasPreview);

    const onClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            onTileClick();
        },
        [onTileClick],
    );

    return (
        <div
            ref={wrapperRef}
            className={classNames(styles['blueprint-preview-tile'], 'p-l5-16', className)}
            onClick={onClick}
            data-testid={isSuggestedBlueprint ? '' : 'customer-blueprint-preview-tile'}
        >
            <div
                className={`${styles['blueprint-preview-tile__canvas']} mb-l5-16 mx-l5-16 position-relative`}
            >
                {previewImageUrl ? (
                    <img
                        src={previewImageUrl}
                        alt={name}
                        className={classNames('d-block mx-l5-auto h-100', aspectRatio)}
                        style={{ objectFit: 'contain', maxWidth: '100%' }}
                    />
                ) : (
                    <StackedCanvasPreview
                        className={classNames(
                            'contained-background d-block mx-l5-auto h-100',
                            aspectRatio,
                        )}
                        sourceCanvas={canvasPreview}
                        buttonText={buttonText}
                    />
                )}
            </div>
            <div className="d-flex align-items-center">
                {isSuggestedBlueprint && (
                    <i className="material-symbols-outlined icon-l5 icon-l5--medium me-l5-4 color-primary">
                        auto_awesome
                    </i>
                )}
                <Tooltip
                    onlyShowOnTruncate
                    title={name}
                    placement={Placement.BOTTOM}
                    container={wrapperRef.current}
                >
                    <div className="fw-semibold font-l5 font-l5--h6 text-truncate">{name}</div>
                </Tooltip>
            </div>
            {(narrativeThread || duration) && (
                <div className="color-grey-700 pt-l5-4 d-flex align-items-center blueprint-properties">
                    {narrativeThread && (
                        <>
                            <i className="material-symbols-outlined icon-l5 icon-l5--medium me-l5-2">
                                {NARRATIVE_THREAD_ICON_MAP[narrativeThread]}
                            </i>{' '}
                            {NARRATIVE_THREAD_DISPLAY_STRING_MAP[narrativeThread]}
                        </>
                    )}
                    {duration && (
                        <>
                            <i className="material-symbols-outlined icon-l5 icon-l5--medium ms-l5-16 me-l5-2">
                                timer
                            </i>
                            {duration}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
